import { fromUnixTime, getUnixTime } from 'date-fns';

import { SPECIAL_DATE_RANGES } from '/src/features/_global/constants';

const VALID_SPECIAL_DATE_RANGES = new Set(Object.values(SPECIAL_DATE_RANGES));

function missing() {
  return { status: 'missing', value: null };
}

function invalid() {
  return { status: 'invalid', value: null };
}

function valid(value) {
  return { status: 'valid', value };
}

function isAbsoluteDateRange(value) {
  return value && Number.isInteger(value.start_date) && Number.isInteger(value.end_date);
}

export function createJsonDateRangeAdapter() {
  return {
    parse(rawValue) {
      if (!rawValue) return missing();

      let parsed;
      try {
        parsed = JSON.parse(rawValue);
      } catch {
        return invalid();
      }

      if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
        return invalid();
      }

      if (parsed.special) {
        if (!VALID_SPECIAL_DATE_RANGES.has(parsed.special)) {
          return invalid();
        }

        return valid({ special: parsed.special });
      }

      if (!isAbsoluteDateRange(parsed) || parsed.start_date > parsed.end_date) {
        return invalid();
      }

      return valid({
        start_date: fromUnixTime(parsed.start_date),
        end_date: fromUnixTime(parsed.end_date),
      });
    },

    serialize(dateRange) {
      if (!dateRange) return null;

      if (dateRange.special) {
        return JSON.stringify({ special: dateRange.special });
      }

      if (!dateRange.start_date || !dateRange.end_date) return null;

      return JSON.stringify({
        start_date: getUnixTime(dateRange.start_date),
        end_date: getUnixTime(dateRange.end_date),
      });
    },
  };
}

export function parseRecordingsFilterDateRange(filter) {
  if (!filter || !Array.isArray(filter.conditions)) {
    return missing();
  }

  const dateRangeCondition = filter.conditions.find((condition) => condition.criteria === 'date_range');

  if (!dateRangeCondition) {
    return missing();
  }

  const { value } = dateRangeCondition;

  if (value?.special) {
    if (!VALID_SPECIAL_DATE_RANGES.has(value.special)) {
      return invalid();
    }

    return valid({ special: value.special });
  }

  if (!isAbsoluteDateRange(value) || value.start_date > value.end_date) {
    return invalid();
  }

  return valid({
    start_date: fromUnixTime(value.start_date),
    end_date: fromUnixTime(value.end_date),
  });
}

export const JSON_DATE_RANGE_ADAPTER = createJsonDateRangeAdapter();
